import { LiksDropdown } from '../../components/liks/LiksDropdown/LiksDropdown';
import { LiksFormFieldLabel } from '../../components/liks/LiksFormFieldLabel/LiksFormFieldLabel';
import { LiksTextFormField } from '../../components/liks/LiksTextFormField/LiksTextFormField';
import { useI18n } from '../../i18n/useI18n';
import type { LacClientRole } from './lacRoles';

type TenantOption = {
  value: string;
  label: string;
};

type CreateLacUserDialogOrganisationFieldsProps = {
  tenantFieldId: string;
  roleFieldId: string;
  isTenantFixed: boolean;
  fixedTenantDisplayValue: string;
  isTenantsLoading: boolean;
  isTenantsError: boolean;
  tenantsLength: number;
  isCreatingNewTenant: boolean;
  tenantSelectValue: string;
  tenantOptions: TenantOption[];
  newTenantName: string;
  onNewTenantNameChange: (value: string) => void;
  onTenantChange: (value: string) => void;
  isDuplicateTenantName: boolean;
  role: LacClientRole;
  roleOptions: TenantOption[];
  onRoleChange: (value: string) => void;
  isSubmitting: boolean;
};

export function CreateLacUserDialogOrganisationFields({
  tenantFieldId,
  roleFieldId,
  isTenantFixed,
  fixedTenantDisplayValue,
  isTenantsLoading,
  isTenantsError,
  tenantsLength,
  isCreatingNewTenant,
  tenantSelectValue,
  tenantOptions,
  newTenantName,
  onNewTenantNameChange,
  onTenantChange,
  isDuplicateTenantName,
  role,
  roleOptions,
  onRoleChange,
  isSubmitting,
}: CreateLacUserDialogOrganisationFieldsProps) {
  const { translate } = useI18n();

  return (
    <div className="lac-create-user__row">
      <div className="liks-text-form-field">
        <LiksFormFieldLabel htmlFor={tenantFieldId} required>
          {translate('lac.users.create.organisationLabel')}
        </LiksFormFieldLabel>
        {isTenantFixed ? (
          <input
            id={tenantFieldId}
            className="liks-text-form-field__input"
            type="text"
            value={fixedTenantDisplayValue}
            disabled
            readOnly
          />
        ) : isTenantsLoading ? (
          <p className="lac-create-user__hint">
            {translate('lac.users.create.organisationsLoading')}
          </p>
        ) : isTenantsError ? (
          <p className="lac-create-user__hint" role="alert">
            {translate('lac.users.create.organisationsLoadError')}
          </p>
        ) : (
          <>
            {tenantsLength > 0 && (
              <LiksDropdown
                id={tenantFieldId}
                className="lac-create-user__dropdown"
                variant="on-light"
                value={tenantSelectValue}
                onChange={onTenantChange}
                options={tenantOptions}
                aria-label={translate('lac.users.create.organisationLabel')}
                disabled={isSubmitting}
              />
            )}

            {isCreatingNewTenant && (
              <LiksTextFormField
                nested={tenantsLength > 0}
                label={translate('lac.users.create.newOrganisationNameLabel')}
                required
                value={newTenantName}
                onChange={onNewTenantNameChange}
                autoFocus={tenantsLength === 0}
                disabled={isSubmitting}
                autoComplete="off"
                error={
                  isDuplicateTenantName
                    ? translate('lac.users.create.newOrganisationNameDuplicate')
                    : null
                }
              />
            )}
          </>
        )}
      </div>

      <div className="liks-text-form-field">
        <LiksFormFieldLabel htmlFor={roleFieldId} required>
          {translate('lac.users.roleLabel')}
        </LiksFormFieldLabel>
        <LiksDropdown
          id={roleFieldId}
          className="lac-create-user__dropdown"
          variant="on-light"
          value={role}
          onChange={onRoleChange}
          options={roleOptions}
          aria-label={translate('lac.users.roleLabel')}
          disabled={isSubmitting}
        />
      </div>
    </div>
  );
}
